import { useId } from "react"
import type { ReactNode } from "react"

import { tv, type VariantProps } from "@/lib/utils/tv"

const field = tv({
  slots: {
    root: "font-text flex flex-col gap-150",
    label: "font-display text-body-sm text-fg font-medium",
    control: "rounded-6x border-field bg-surface transition-colors focus-within:border-line-brand focus-within:ring-3 focus-within:ring-brand/15",
    input: "text-field text-fg placeholder:text-fg-muted w-full self-stretch border-0 bg-transparent outline-none",
    help: "text-body-sm text-fg-subtle",
  },
  variants: {
    state: {
      default: { control: "border-line" },
      filled: { control: "border-fg" },
      brand: { control: "border-line-brand bg-surface-brand-subtle" },
      error: { control: "border-error-text bg-error-fill", help: "text-error-text" },
    },
    // sm is for dense desktop-only surfaces (admin tools); the md text-field
    // 16px floor exists for iOS auto-zoom and stays the storefront default.
    size: {
      md: { control: "min-h-13 px-400" },
      sm: { root: "gap-100", control: "min-h-9 px-300", input: "text-body", help: "text-caption" },
      lg: {
        root: "gap-0",
        label: "text-onboarding-sm text-fg-heading pb-200",
        control: "min-h-16 rounded-8x border px-400",
        input: "text-control-lg placeholder:text-fg-placeholder",
        help: "text-onboarding-sm text-fg-secondary pt-200",
      },
    },
    shape: { rounded: {}, pill: { control: "rounded-full pr-200" } },
    disabled: { true: { control: "cursor-not-allowed border-line-subtle bg-surface-newgray" } },
  },
  compoundVariants: [
    { size: "lg", state: "default", class: { control: "border-line-sand" } },
    { size: "lg", state: "brand", class: { control: "bg-surface-baby-blue" } },
    { size: "lg", state: "error", class: { control: "border-line-error bg-surface-error-soft", help: "text-line-error" } },
  ],
  defaultVariants: { state: "default", size: "md", shape: "rounded" },
})

export type FieldState = VariantProps<typeof field>["state"]
export type FieldSize = VariantProps<typeof field>["size"]
export type FieldShape = VariantProps<typeof field>["shape"]

// Shared id/aria/style plumbing for the form controls (text, textarea, select)
// so the three stay identical. Disabled neutralizes the visual state.
export const useFieldA11y = ({
  id,
  help,
  state,
  size,
  shape,
  disabled,
}: {
  id?: string
  help?: ReactNode
  state?: FieldState
  size?: FieldSize
  shape?: FieldShape
  disabled?: boolean
}) => {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const helpId = `${fieldId}-help`
  const styles = field({ state: disabled ? "default" : state, size, shape, disabled })
  return {
    fieldId,
    helpId,
    styles,
    controlProps: {
      id: fieldId,
      "aria-invalid": state === "error" || undefined,
      "aria-describedby": help ? helpId : undefined,
    },
  }
}
